import React, { useEffect, useMemo, useState } from 'react';
import type { NavigationProp } from '@react-navigation/native';
import { useBranchPermission } from '../../../shell/session/hooks/useBranchPermission'
import { useModulePermission } from '../../../shell/session/hooks/useModulePermission';
import { PosTrialBanner } from '../../components/PosTrialBanner';
import { usePlanEntitlements } from '../../../shell/providers/PlanEntitlementsProvider';
import { planAllowsCustomer } from '../../../shell/feature-flags/planEntitlementsService';
import {
  parseActivePlanGrant,
  planDisplayName,
  trialBannerSubtitle,
  trialUrgencyPhase,
} from '../../../../../../shared/billing-catalog';
import { getDefaultPosFiscalMode, type PosFiscalDefaultStrategy } from '../../../fiscal/domain-api';
import { allowsHospitality } from '../../../shell/feature-flags/businessCapabilitiesService';
import { useHasBusinessCapability } from '../../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { captureCatch } from '../../../shared/sentry/sentryUser';
import { useBusinessProfileStore } from '../../../shell/session/store/useBusinessProfileStore';
import { useGlobalFeatureFlags } from '../../../shell/providers/GlobalFeatureFlagsProvider';
import { isGlobalFeatureOn } from '../../../shell/feature-flags/globalFeatureFlagsService';
import { navigateInShell } from '../../../shell/navigation/rootNavigation';
import { isBillingEnabled } from '../../../shell/billing/isBillingEnabled';
import { getKnownSessionOrganizationId, getSessionOrganizationId } from '../../../shell/session/sessionOrganization';
import { useBranchSalesHistory } from '../../../sales/public-api';
import { getCachedMpIntegrationStatus } from '../../../settings/application/storefrontStatus';

type Nav = NavigationProp<Record<string, object | undefined>>;

type Params = {
  navigation: Nav;
  currentBranchId: string | null | undefined;
  sessionUserId: string | null | undefined;
};

export function usePosPermissionsPane({
  navigation,
  currentBranchId,
  sessionUserId,
}: Params) {
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const { entitlements, loading: entitlementsLoading } = usePlanEntitlements();
  const { flags: globalFeatureFlags } = useGlobalFeatureFlags();
  const [mpPosConfigured, setMpPosConfigured] = useState<boolean>(false);

  useEffect(() => {
    let alive = true;
    setMpPosConfigured(false);
    if (!currentBranchId) {
      return () => {
        alive = false;
      };
    }
    void getCachedMpIntegrationStatus(currentBranchId)
      .then((status) => {
        if (alive) setMpPosConfigured(status.oauth_connected === true && status.pos_configured === true);
      })
      .catch(captureCatch('pos', 'mp-integration-status'));
    return () => {
      alive = false;
    };
  }, [currentBranchId]);

  const mercadopagoQrMethodAllowed = useMemo(() => {
    if (!isGlobalFeatureOn(globalFeatureFlags, 'mercadopago_integrations')) return false;
    if (entitlementsLoading || !entitlements) return false;
    if (entitlements.mp_qr_pos !== true) return false;
    return mpPosConfigured;
  }, [entitlements, entitlementsLoading, globalFeatureFlags, mpPosConfigured]);

  const allowFiscalSaleMode = useMemo(() => {
    if (!isGlobalFeatureOn(globalFeatureFlags, 'arca_integrations')) return false;
    if (entitlementsLoading || !entitlements) return false;
    return entitlements.fiscal_enabled === true;
  }, [entitlements, entitlementsLoading, globalFeatureFlags]);

  const defaultCheckoutFiscalMode = useMemo(
    (): PosFiscalDefaultStrategy => getDefaultPosFiscalMode(entitlements, allowFiscalSaleMode),
    [allowFiscalSaleMode, entitlements]
  );

  const canSellOnCredit = useModulePermission('POS', 'sell_on_credit');
  const canDiscount = useModulePermission('POS', 'discount');

  const customerMethodAllowed = useMemo(() => {
    if (!canSellOnCredit) return false;
    if (!isGlobalFeatureOn(globalFeatureFlags, 'fiado_integrations')) return false;
    if (entitlementsLoading) return false;
    return planAllowsCustomer(entitlements);
  }, [canSellOnCredit, entitlements, entitlementsLoading, globalFeatureFlags]);

  const trialGrant = useMemo(
    () => parseActivePlanGrant(entitlements?.active_plan_grant),
    [entitlements?.active_plan_grant]
  );

  const trialBanner = useMemo(() => {
    // billing disabled — free mode: keep PosTrialBanner module but do not mount it
    if (!isBillingEnabled()) return null;
    if (!trialGrant || entitlements?.is_owner !== true) return null;
    const planName = planDisplayName(trialGrant.plan_code);
    const urgency = trialUrgencyPhase(trialGrant.expires_at);
    return (
      <PosTrialBanner
        planName={planName}
        subtitle={trialBannerSubtitle(trialGrant.expires_at, planName)}
        urgency={urgency === 'expired' ? 'last_day' : urgency}
        ctaLabel="Suscribirme"
        onPressCta={() => navigateInShell(navigation, 'Billing')}
      />
    );
  }, [trialGrant, entitlements?.is_owner, navigation]);

  const businessProfile = useBusinessProfileStore((s) => s.profile);
  const { canEdit, canView: canViewPos } = useBranchPermission('POS');
  const { canEdit: canEditCatalog } = useBranchPermission('Catalog');
  const canImportFromCatalog = canEdit && canEditCatalog;
  const { canView: canViewReports } = useBranchPermission('Reports');
  const canOpenCashManagementFromPos = canEdit || canViewReports;

  const hasTablesCapability = useHasBusinessCapability('tables');
  const hasKitchenCapability = useHasBusinessCapability('kitchen_tickets');
  const checkMethodAllowed = useHasBusinessCapability('checks');
  const hospitalityEnabled = allowsHospitality(businessProfile);

  const { openSalesHistory } = useBranchSalesHistory();

  useEffect(() => {
    let alive = true;
    const known = getKnownSessionOrganizationId();
    if (known) {
      setOrganizationId(known);
      return () => {
        alive = false;
      };
    }
    void getSessionOrganizationId()
      .then((orgId) => {
        if (alive && orgId) setOrganizationId(orgId);
      })
      .catch(captureCatch('pos', 'permissions-session-organization'));
    return () => {
      alive = false;
    };
  }, [sessionUserId]);

  return {
    organizationId,
    entitlements,
    mercadopagoQrMethodAllowed,
    allowFiscalSaleMode,
    defaultCheckoutFiscalMode,
    customerMethodAllowed,
    checkMethodAllowed,
    canDiscount,
    trialBanner,
    canEdit,
    canViewPos,
    canImportFromCatalog,
    canOpenCashManagementFromPos,
    businessProfile,
    hasTablesCapability,
    hasKitchenCapability,
    hospitalityEnabled,
    openSalesHistory,
  };
}
